import { Injectable } from '@angular/core';
import { FormControl, FormGroup, FormArray, Validators } from '@angular/forms';
import { PricingModelEnum } from '@shared/models/enums/pricingModel.enum';

@Injectable({ providedIn: 'root' })
export class GeneralInfoForm extends FormGroup {
  constructor() {
    super({
      name: new FormControl('', { validators: Validators.required, nonNullable: true }),
      bookings: new FormControl<any[]>([], { validators: Validators.required, nonNullable: true }),
    });
  }

  resetForm() {
    this.reset();
  }
}

@Injectable({ providedIn: 'root' })
export class FiltersForm extends FormGroup {
  constructor() {
    super({
      customer: new FormControl(0, { nonNullable: true }),
      channels: new FormControl<string[]>([], { nonNullable: true }),
      placements: new FormControl<string[]>([], { nonNullable: true }),
    });
  }

  resetForm() {
    this.reset();
  }
}

@Injectable({ providedIn: 'root' })
export class MediaFormGroup extends FormGroup {
  constructor() {
    super({
      advertisementDetails: new FormArray([], Validators.required),
      defaultMediaDetails: new FormControl<null | string>(null, { nonNullable: false }),
      defaultMediaPresentationTime: new FormControl<null | number>(null, { nonNullable: false }),
      presentationTime: new FormControl<null | number>(null, { nonNullable: false }),
    });
  }

  resetForm() {
    this.reset();

    const adFormArray = this.get('advertisementDetails') as FormArray;
    adFormArray.clear();
  }
}

@Injectable({ providedIn: 'root' })
export class MarketingAutomationFormGroup extends FormGroup {
  constructor() {
    super({
      isEnabled: new FormControl<boolean | null>(null),
      marketingAutomation: new FormGroup({
        childPlayListId: new FormControl<number | null>(null),
        engagementName: new FormControl<string | null>(null),
        playlistMediaFiles: new FormArray([], Validators.required),
        hasAdvancedSettings: new FormControl<boolean | null>(null),
        advancedSettings: new FormGroup({
          numberOfPeople: new FormControl<number | null>(null),
          numberOfMinutes: new FormControl<number | null>(null),
          configurationRuleOneActivated: new FormControl<boolean | null>(null),
          configurationRuleTwoActivated: new FormControl<boolean | null>(null),
        }),
        sensitivitySettings: new FormControl<string | null>(null),
      }),
    });
  }

  resetForm() {
    this.reset();
  }
}

@Injectable({ providedIn: 'root' })
export class AdvertisingFormGroup extends FormGroup {
  // CPV pricing needs sensor view counts; set when every booked screen is ScreenOnly (#3761).
  allScreensScreenOnly = false;

  constructor() {
    super({
      media: new FormGroup({
        bookingId: new FormControl<boolean>(null),
        hasAdvertisingPricing: new FormControl<boolean>(false, { nonNullable: false }),
        presentationTime: new FormControl<null | number>(null),
        advertisementDetails: new FormArray([]),
      }),
      marketingAutomation: new FormGroup({
        bookingId: new FormControl<boolean>(null),
        hasMarketingAutomation: new FormControl<boolean>(false, { nonNullable: false }),
        presentationTime: new FormControl<null | number>(null),
        marketingAutomationDetails: new FormArray([]),
      })
    });

    this.addValidators(() => (this.allScreensScreenOnly && this.hasCpvPricedItem() ? { cpvNotAllowed: true } : null));
  }

  private hasCpvPricedItem(): boolean {
    if (!this.get('media.hasAdvertisingPricing')?.value) {
      return false;
    }

    const details = [
      ...(this.get('media.advertisementDetails') as FormArray).controls,
      ...(this.get('marketingAutomation.marketingAutomationDetails') as FormArray).controls,
    ];

    return details.some(item => item.get('mediaDetails.mediaPricingDetails.pricingModel')?.value === PricingModelEnum.CPV);
  }

  resetForm() {
    this.reset();
    this.allScreensScreenOnly = false;

    const advertisementDetails = this.get('media.advertisementDetails') as FormArray;
    advertisementDetails.clear();

    const marketingAutomationDetails = this.get('marketingAutomation.marketingAutomationDetails') as FormArray;
    marketingAutomationDetails.clear();
  }
}

export function toggleEnableDisableForm(form: FormGroup | null, canEdit: boolean, controls?: string[]) {
  if (!form) {
    return;
  }

  if (!controls || controls.length === 0) {
    form[canEdit ? 'enable' : 'disable']({ emitEvent: false });
    return;
  }
  controls.forEach(c => form.get(c)?.[canEdit ? 'enable' : 'disable']({ emitEvent: false }));
}
